import { useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthState } from "@/types/auth";
import { getGuestStatus, clearGuestStatus } from "@/utils/guestDetection";

export function useAuthRedirect() {
  const navigate = useNavigate();
  const isMountedRef = useRef(true);

  const redirectBasedOnAuthState = useCallback((authState: AuthState) => {
    // Reset mounted ref when callback is called (component is mounting/updating)
    isMountedRef.current = true;
    
    // Check if all auth checks should be skipped
    if (localStorage.getItem('skipAuthChecks') === 'true') {
      console.log("🚫 All auth checks disabled");
      return () => {
        isMountedRef.current = false;
      };
    }

    // Check if auth redirect is disabled
    if (localStorage.getItem('disableAuthRedirect') === 'true') {
      console.log("🚫 Auth redirect disabled");
      return () => {
        isMountedRef.current = false;
      };
    }

    // Check if we're in the middle of a navigation
    if (localStorage.getItem('navigatingToSession') === 'true') {
      console.log("🚫 Navigation in progress, skipping auth redirect");
      return () => {
        isMountedRef.current = false;
      };
    }

    if (!authState.initialized || authState.loading) {
      console.log("🔄 Auth not ready for redirect", { 
        initialized: authState.initialized, 
        loading: authState.loading,
        hasUser: !!authState.user,
        hasSession: !!authState.session 
      });
      return () => {
        isMountedRef.current = false;
      };
    }

    const currentPath = window.location.pathname;
    console.log("🚨 REDIRECT CHECK: Current state:", { 
      currentPath, 
      hasSession: !!authState.session,
      hasProfile: !!authState.profile,
      profileType: authState.profile?.profile_type,
      userId: authState.user?.id,
      profileData: authState.profile
    });
    
    // CRITICAL: Don't redirect if already on /auth, /auth/callback, /onboarding, or /join pages
    // This must be the first check after getting currentPath to prevent any redirect logic from running
    // /auth/callback must be accessible to authenticated users for scope upgrades (REQ-51)
    if (currentPath === "/auth" || currentPath === "/auth/callback" || currentPath === "/onboarding" || currentPath === "/join") {
      console.log("🔍 [useAuthRedirect] Current path:", currentPath, "Session exists:", !!authState.session);
      console.log("✅ Already on auth/auth-callback/onboarding/join page, no redirect needed");
      return () => {
        isMountedRef.current = false;
      };
    }

    // Check for navigation flags set by GuestUpsellModal or Auth page
    // These flags allow guests to navigate to auth or home without being redirected back
    const allowAuthNavigation = localStorage.getItem('allowAuthNavigation') === 'true';
    const allowHomeNavigation = localStorage.getItem('allowHomeNavigation') === 'true';
    
    // Check if we're navigating TO auth (not just if we're already on it)
    if (allowAuthNavigation) {
      console.log("✅ Navigation to auth allowed from guest upsell modal");
      // Clear the flag after navigation completes
      setTimeout(() => localStorage.removeItem('allowAuthNavigation'), 2000);
      // Don't redirect if we're going to /auth
      if (currentPath === "/auth" || window.location.pathname === "/auth") {
        return () => {
          isMountedRef.current = false;
        };
      }
    }
    
    if (allowHomeNavigation && (currentPath === "/" || currentPath === "/dashboard")) {
      console.log("✅ Navigation to home allowed from guest upsell modal or auth page");
      // Clear the flag after a short delay
      const homeNavTimeout = setTimeout(() => localStorage.removeItem('allowHomeNavigation'), 2000);
      return () => {
        clearTimeout(homeNavTimeout);
        isMountedRef.current = false;
      };
    }

    // Check if this is an anonymous user (guest)
    // A user is anonymous if they have no email OR explicitly marked as anonymous
    // If user has an email, they are NOT anonymous, even if they have guest status
    const isAnonymous = authState.user && (
      (authState.user as any).is_anonymous === true ||
      (!authState.user.email && !authState.profile?.email)
    );
    
    // If user has email, they are authenticated - clear any stale guest status
    if (authState.user?.email && !isAnonymous) {
      const guestStatus = getGuestStatus();
      if (guestStatus?.isGuest) {
        console.log("🧹 Clearing stale guest status for authenticated user");
        clearGuestStatus();
      }
    }

    // Check if this is a guest user - but only if user doesn't have a completed profile
    // If user has completed onboarding, they're not a guest and guest status should be ignored
    if (!authState.profile?.onboarding_completed && !authState.profile?.profile_type) {
      const guestStatus = getGuestStatus();
      
      // If user is anonymous, check guest status or redirect to join
      if (isAnonymous) {
        if (guestStatus?.isGuest) {
          // Guest with valid status - allow access to session pages only
          if (currentPath.startsWith("/session/") || currentPath.startsWith("/greenroom/") || currentPath === "/join") {
            console.log("🎭 Anonymous guest user with valid status, allowing access to session/join pages");
            return () => {
              isMountedRef.current = false;
            };
          } else if (currentPath === "/dashboard" || currentPath === "/") {
            // Check if homepage navigation is allowed (from Auth page)
            if (allowHomeNavigation) {
              console.log("✅ Homepage navigation allowed, skipping redirect");
              return () => {
                isMountedRef.current = false;
              };
            }
            
            // Redirect anonymous users away from dashboard
            if (guestStatus.sessionId) {
              console.log("🎭 Redirecting anonymous guest to their session");
              if (isMountedRef.current) {
                navigate(`/session/${guestStatus.sessionId}`);
              }
            } else if (guestStatus.inviteCode) {
              console.log("🎭 Redirecting anonymous guest to join page with invite code");
              if (isMountedRef.current) {
                navigate(`/join?invite=${guestStatus.inviteCode}`);
              }
            } else {
              console.log("🎭 Redirecting anonymous guest to join page");
              if (isMountedRef.current) {
                navigate("/join");
              }
            }
            return () => {
              isMountedRef.current = false;
            };
          }
        } else {
          // Anonymous user without guest status - redirect to join (but not if already on /auth, /onboarding, or /join)
          // Note: The early return above already handles /auth, /onboarding, and /join, so we only redirect from other pages
          console.log("🎭 Anonymous user without guest status, redirecting to join page");
          if (isMountedRef.current) {
            navigate("/join");
          }
          return () => {
            isMountedRef.current = false;
          };
        }
      } else if (guestStatus?.isGuest) {
        // Non-anonymous user with guest status (shouldn't happen, but handle it)
        console.log("🎭 Guest user detected in auth redirect, allowing access");
        return () => {
          isMountedRef.current = false;
        };
      }
    } else {
      // User has completed onboarding - clear any stale guest status
      const guestStatus = getGuestStatus();
      if (guestStatus?.isGuest) {
        console.log("🧹 Clearing stale guest status for authenticated user with completed profile");
        clearGuestStatus();
      }
      
      // Prevent anonymous users from accessing dashboard even if they have a profile
      if (isAnonymous && (currentPath === "/dashboard" || currentPath === "/")) {
        console.log("🚫 Anonymous user attempting to access dashboard, redirecting to join");
        if (isMountedRef.current) {
          navigate("/join");
        }
        return () => {
          isMountedRef.current = false;
        };
      }
    }

    // Don't redirect if navigating to a session page
    if (currentPath.startsWith("/session/")) {
      console.log("✅ Navigating to session page, no redirect needed");
      // Clear the session flag
      localStorage.removeItem('navigatingToSession');
      return () => {
        isMountedRef.current = false;
      };
    }

    // Don't redirect if we're in the process of navigating to a session
    if (localStorage.getItem('navigatingToSession') === 'true') {
      console.log("✅ Navigating to session (flag set), no redirect needed");
      // Clear the flag after a short delay to prevent it from persisting
      const sessionNavTimeout = setTimeout(() => {
        localStorage.removeItem('navigatingToSession');
      }, 1000);
      return () => {
        clearTimeout(sessionNavTimeout);
        isMountedRef.current = false;
      };
    }

    // Don't redirect if we're in the process of navigating (check for recent navigation)
    const lastNavigation = localStorage.getItem('lastNavigation');
    const now = Date.now();
    if (lastNavigation && (now - parseInt(lastNavigation)) < 2000) {
      console.log("✅ Recent navigation detected, no redirect needed");
      // Clean up old timestamp
      if (now - parseInt(lastNavigation) > 1000) {
        localStorage.removeItem('lastNavigation');
      }
      return () => {
        isMountedRef.current = false;
      };
    }

    // Only redirect after a small delay to prevent loops
    const timeoutId = setTimeout(() => {
      // Check if component is still mounted before calling navigate
      if (!isMountedRef.current) {
        console.log("🚫 Component unmounted, skipping redirect");
        return;
      }
      
      // Check if we're in the middle of signing out
      if (localStorage.getItem('authSignOutInProgress') === 'true' || (window as any).__authSignOutInProgress) {
        console.log("🚫 Sign out in progress, skipping redirect");
        return;
      }

      // Don't redirect if we're already on the login page
      if (currentPath === "/login") {
        console.log("✅ Already on login page, no redirect needed");
        return;
      }

      if (!authState.session || !authState.user) {
        console.log("🔄 No session/user, redirecting to auth");
        if (isMountedRef.current) {
          navigate("/auth");
        }
        return;
      }

      // Only redirect to onboarding if user has email (authenticated), not anonymous
      if (authState.user.email && !authState.profile?.onboarding_completed && !authState.profile?.profile_type) {
        console.log("🔄 No onboarding completion, redirecting to onboarding");
        if (isMountedRef.current) {
          navigate("/onboarding");
        }
        return;
      }

      console.log("✅ Auth state valid, user can stay on current page");
    }, 50);

    return () => {
      clearTimeout(timeoutId);
      isMountedRef.current = false;
    };
  }, [navigate]);

  return { redirectBasedOnAuthState };
}

export default useAuthRedirect;